import type { ComponentType } from 'react';
import type { CampaignTemplate } from '@/connection/campaigns/fetch-campaign-templates';
import type { BrandliveEvent } from '@/types/projects.types';
import { ThemePack, EPaletteModes, IColorVariants } from '@/types/theme-packs.types';
import type { LanguagesAbbr } from '@/types/translation.types';
import type { EmailEditorFields } from '@/utils/scheduled-email-campaign';
import { Basic } from './basic.tsx';
import { Salon } from './salon.tsx';
import { Zoo } from './zoo.tsx';
import { Dolphin } from './dolphin.tsx';
import { Pink } from './pink.tsx';
import { Wedding } from './wedding.tsx';
import { Blue } from './blue.tsx';
import { Flat } from './flat.tsx';
import { Green } from './green.tsx';
import { Hollywood } from './hollywood.tsx';
import { News } from './news.tsx';
import { Drab } from './drab.tsx';
import { WesAnderson } from './wesanderson.tsx';
import type { EmailCalendarLinks } from '@/types/email.types.ts';
import chroma from 'chroma-js';
import getRelativeContrastColor from '@/utils/colors/generate-relative-contrasting-color.ts';

export interface TemplateProps {
  event: BrandliveEvent;
  theme: ThemePack;
  language?: LanguagesAbbr;
  mode?: EPaletteModes;
  href?: string;
  calendarLinks?: EmailCalendarLinks | undefined;
  customizations?: EmailEditorFields;
  salutation?: string;
  setSalutation?: (str: string) => void;
  title?: string;
  setTitle?: (str: string) => void;
  description?: string;
  setDescription?: (str: string) => void;
  cta?: string;
  setCta?: (str: string) => void;
  startIntro?: string;
  setStartIntro?: (str: string) => void;
  setMainImage?: (str: string) => void;
  setHeaderImage?: (str: string) => void;
}

export interface LocalCampaignTemplate extends CampaignTemplate {
  component: ComponentType<TemplateProps>;
}

export const campaignTemplates: CampaignTemplate[] = [
  { uuid: 'basic', title: 'Basic', category: 'Classic' },
  { uuid: 'salon', title: 'Salon', category: 'Salon' },
  { uuid: 'zoo', title: 'Zoo', category: 'Zoo' },
  { uuid: 'dolphin', title: 'Dolphin', category: 'Elegant' },
  { uuid: 'pink', title: 'Pink', category: 'Elegant' },
  { uuid: 'wedding', title: 'Wedding', category: 'Wedding' },
  { uuid: 'blue', title: 'Blue', category: 'Modern' },
  { uuid: 'flat', title: 'Flat', category: 'Graphic' },
  { uuid: 'green', title: 'Green', category: 'Editorial' },
  { uuid: 'hollywood', title: 'Hollywood', category: 'Hollywood' },
  { uuid: 'news', title: 'News', category: 'News' },
  { uuid: 'drab', title: 'Drab', category: 'Minimal' },
  { uuid: 'wesanderson', title: 'Wes Anderson', category: 'Artistic' },
];

export const campaignTemplatesMap: Map<string, ComponentType<TemplateProps>> = new Map([
  ['basic', Basic],
  ['salon', Salon],
  ['zoo', Zoo],
  ['dolphin', Dolphin],
  ['pink', Pink],
  ['wedding', Wedding],
  ['blue', Blue],
  ['flat', Flat],
  ['green', Green],
  ['hollywood', Hollywood],
  ['news', News],
  ['drab', Drab],
  ['wesanderson', WesAnderson],
]);

export type CampaignColorContrastPair = [back: keyof EmailColors, front: keyof EmailColors];

export interface CampaignTemplateDefault {
  salutation?: string;
  startIntro?: string;
  cta?: string;
  headingFont?: string;
  bodyFont?: string;
  contrasts?: CampaignColorContrastPair[];
}

export interface EmailColors {
  backgroundColor: string;
  bodyTextColor: string | undefined;
  containerColor: string | undefined;
  accentColor: string | undefined;
  headingTextColor: string | undefined;
  secondaryBackgroundColor: string | undefined;
  secondaryAccentColor: string | undefined;
  buttonTextColor: string | undefined;
}

export const createContrastOverrides = (
  colors: IColorVariants | undefined,
  defaults: CampaignTemplateDefault | undefined,
  currentMode: EPaletteModes
) => {
  if (defaults && colors) {
    const background = chroma(colors?.backgroundColor[0] ?? (currentMode === EPaletteModes.Dark ? '#000' : '#fff'));
    let bgColor;
    if (currentMode === EPaletteModes.Dark) {
      bgColor = background.shade(0.5).hex();
    } else {
      bgColor = background.tint(0.5).hex();
    }

    const overrides: EmailColors = {
      backgroundColor: bgColor,
      bodyTextColor: colors?.bodyTextColor[0],
      containerColor: colors?.containerColor[0],
      accentColor: colors?.accentColor[0],
      headingTextColor: colors?.headingTextColor[0],
      secondaryBackgroundColor: colors?.secondaryBackgroundColor[0],
      secondaryAccentColor: colors?.secondaryAccentColor[0],
      buttonTextColor: colors?.buttonTextColor?.[0],
    };

    if (defaults.contrasts) {
      for (const [back, front] of defaults.contrasts) {
        if (colors[front]?.[0] && colors[back]?.[0]) {
          overrides[front] = getRelativeContrastColor(colors[front][0], colors[back][0]);
        }
      }
    }

    return overrides;
  }
};

export const campaignTemplatesDefaults: Map<string, CampaignTemplateDefault> = new Map([
  [
    'basic',
    {
      salutation: "YOU'VE BEEN INVITED TO",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Arial',
      bodyFont: 'Arial',
      contrasts: [
        ['containerColor', 'buttonTextColor'],
        ['containerColor', 'bodyTextColor'],
      ],
    },
  ],
  [
    'salon',
    {
      salutation: "YOU'VE BEEN INVITED TO",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Arial',
      bodyFont: 'Arial',
      contrasts: [['containerColor', 'bodyTextColor']],
    },
  ],
  [
    'zoo',
    {
      salutation: "YOU'RE INVITED TO",
      startIntro: '',
      cta: 'REGISTER',
      headingFont: 'Impact',
      bodyFont: 'Verdana',
      contrasts: [
        ['accentColor', 'bodyTextColor'],
        ['accentColor', 'headingTextColor'],
        ['secondaryAccentColor', 'buttonTextColor'],
      ],
    },
  ],
  [
    'dolphin',
    {
      salutation: "You've been invited to",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Georgia',
      bodyFont: 'Trebuchet MS',
      contrasts: [
        ['containerColor', 'bodyTextColor'],
        ['accentColor', 'headingTextColor'],
        ['secondaryAccentColor', 'buttonTextColor'],
      ],
    },
  ],
  [
    'pink',
    {
      salutation: "YOU'VE BEEN INVITED TO",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Georgia',
      bodyFont: 'Trebuchet MS',
      contrasts: [
        ['containerColor', 'bodyTextColor'],
        ['accentColor', 'headingTextColor'],
        ['headingTextColor', 'buttonTextColor'],
      ],
    },
  ],
  [
    'wedding',
    {
      salutation: "YOU'VE BEEN INVITED TO",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Georgia',
      bodyFont: 'Trebuchet MS',
      contrasts: [['containerColor', 'bodyTextColor']],
    },
  ],
  [
    'blue',
    {
      salutation: "You've been invited to:",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Arial Black',
      bodyFont: 'Verdana',
      contrasts: [['containerColor', 'bodyTextColor']],
    },
  ],
  [
    'flat',
    {
      salutation: 'You have been invited to:',
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Tahoma',
      bodyFont: 'Verdana',
      contrasts: [
        ['containerColor', 'bodyTextColor'],
        ['accentColor', 'headingTextColor'],
      ],
    },
  ],
  [
    'green',
    {
      salutation: 'You have been invited to',
      startIntro: '',
      cta: 'REGISTER',
      headingFont: 'Georgia',
      bodyFont: 'Trebuchet MS',
      contrasts: [['containerColor', 'bodyTextColor']],
    },
  ],
  [
    'hollywood',
    {
      salutation: "YOU'RE INVITED TO",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Arial Black',
      bodyFont: 'Trebuchet MS',
      contrasts: [['containerColor', 'bodyTextColor']],
    },
  ],
  [
    'news',
    {
      salutation: "YOU'VE BEEN INVITED TO",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Arial',
      bodyFont: 'Courier New ',
      contrasts: [['containerColor', 'bodyTextColor']],
    },
  ],
  [
    'drab',
    {
      salutation: 'You have been invited to:',
      startIntro: '',
      cta: 'REGISTER',
      headingFont: 'Impact',
      bodyFont: 'Arial',
      contrasts: [['containerColor', 'bodyTextColor']],
    },
  ],
  [
    'wesanderson',
    {
      salutation: "You've been invited to",
      startIntro: 'The show starts on',
      cta: 'REGISTER',
      headingFont: 'Arial Black',
      bodyFont: 'Trebuchet MS',
      contrasts: [
        ['accentColor', 'headingTextColor'],
        ['secondaryBackgroundColor', 'bodyTextColor'],
        ['bodyTextColor', 'buttonTextColor'],
      ],
    },
  ],
]);
